import React, { useEffect, useRef, useState } from "react";
import classes from "./ThreeWayMatching.module.css";
import axios from "axios";
import { API_ENDPOINT } from "../../../services/api/resources";
import { useSelector } from "react-redux";
import { Button } from "@mui/material";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import InvoiceChart from "./Charts/InvoiceCharts";
import VendorChart from "./Charts/VendorOnboardChart";
import VendorStatusPie from "./Charts/StatusPie";
import VendorOnboardChart from "./Charts/VendorOnboardDateChart";
import VendorInvoiceDashboard from "./Charts/VendorInvoiceDash";
import VendorInvoiceTable from "./Charts/VendorInvoiceTable";
import InvoiceStatusTable from "./Charts/InvoiceStatusTable";
import PendingESignatureTable from "./Charts/EsignStatus";
import MerchantInvoiceDelay from "../DashboardTab/MerchantInvoiceDelay";
import CountCards from "./Charts/CountCards";
import { useLocation } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { saveAs } from "file-saver";
import InvoiceSampleItemsList from "../../../assets/files/InvoiceSampleItemsList.xlsx";

const InvoiceAutomation = () => {
  const location = useLocation();
  const loginData = useSelector((state) => state.loginData.value);

  const [selectedFile, setSelectedFile] = useState(null);
  const [invoiceList, setInvoiceList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [invoicepage, setInvoicePage] = useState(false);
  const [userStatus, setUserStatus] = useState({});
  const [exporting, setExporting] = useState(false);

  const invoiceUploadInputRef = useRef(null);
  const pdfRef = useRef();
  const dashboardExportRef = useRef();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    setInvoiceList([]);
    setUploadError("");
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setUploadError("");

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await axios.post(API_ENDPOINT.invoice_generate, formData);

      if (response?.data?.error) {
        setInvoiceList([]);
        setUserStatus({});
        setUploadError(String(response.data.error));
        return;
      }

      const invoices = response.data.invoices || [];
      let progressMap = {};

      for (let i = 0; i < invoices.length; i++) {
        const invoice = invoices[i];

        progressMap[invoice.vendor.email] = `Generating... ${Math.round(
          ((i + 1) / invoices.length) * 100
        )}%`;

        setUserStatus({ ...progressMap });

        await new Promise((resolve) => setTimeout(resolve, 300));

        progressMap[invoice.vendor.email] = "✅ Done!";
        setUserStatus({ ...progressMap });
      }

      setInvoiceList(invoices);
    } catch (error) {
      setInvoiceList([]);
      setUserStatus({});
      setUploadError(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.response?.data?.response ||
          "Upload failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = (invoice) => {
    const byteChars = atob(invoice.pdf_base64);
    const byteArray = new Uint8Array(byteChars.length);

    for (let i = 0; i < byteChars.length; i++) {
      byteArray[i] = byteChars.charCodeAt(i);
    }

    const blob = new Blob([byteArray], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = invoice.filename || "Invoice.pdf";
    a.click();

    URL.revokeObjectURL(url);
  };

  const switchInvoice = () => {
    setInvoicePage((prev) => !prev);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get("action");

    if (action === "create") {
      setInvoicePage(true);
    }

    if (action === "download") {
      downloadScreenAsPDF();
    }
  }, [location.search]);

  const downloadScreenAsPDF = async () => {
    try {
      setExporting(true);

      const exportRoot = pdfRef.current || dashboardExportRef.current;
      if (!exportRoot) return;

      const canvas = await html2canvas(exportRoot, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollX: 0,
        scrollY: 0,
        ignoreElements: (el) =>
          el?.getAttribute?.("data-export-ignore") === "true",
      });

      const pdf = new jsPDF("p", "mm", "a4");
      const imgData = canvas.toDataURL("image/jpeg", 0.95);

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth - 16;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 8;

      pdf.addImage(imgData, "JPEG", 8, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight + 8;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 8, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save("Merchant-Invoice-Dashboard.pdf");
    } catch (err) {
      console.error("PDF export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div ref={pdfRef} className={classes.invoicePageRoot}>
      <div className={classes.headerContainer} data-export-section>
        <HeaderTitle titleText={"Trade Claims Automation"} />

        <div className={classes.headerActions} data-export-ignore>
          {!invoicepage && (
            <Button
              variant="contained"
              onClick={downloadScreenAsPDF}
              disabled={exporting}
              sx={{
                fontSize: "13px",
                fontFamily: "gilroyBold",
                textTransform: "none",
                borderRadius: "8px",
                px: 2.4,
                py: 1,
              }}
            >
              {exporting ? "Exporting..." : "Export Report"}
            </Button>
          )}

          {loginData.access_type !== "Buyer" && (
            <Button
              variant="contained"
              onClick={switchInvoice}
              sx={{
                fontFamily: "gilroyBold",
                fontSize: "13px",
                textTransform: "none",
                borderRadius: "8px",
                px: 2.4,
                py: 1,
              }}
            >
              {!invoicepage ? "Upload Supplier Invoice" : "Back"}
            </Button>
          )}
        </div>
      </div>

      {invoicepage ? (
        <div className={classes.uploadPage}>
          <div className={classes.uploadCard}>
            <div className={classes.uploadHeader}>
              <h2>Merchant Invoice Auto-Generation</h2>
              <p>Upload Excel or CSV file to generate supplier invoices.</p>
            </div>

            <div className={classes.fileUploadWrapper}>
              <div className={classes.file_upload}>
                <div className={classes.DocText}>
                  <span>
                    {selectedFile
                      ? selectedFile.name
                      : "Upload an Excel / CSV file"}
                  </span>
                  <p>Accepted file formats: .csv, .xls, .xlsx</p>
                </div>

                <input
                  ref={invoiceUploadInputRef}
                  type="file"
                  accept=".csv,.xls,.xlsx"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />

                {!selectedFile ? (
                  <button
                    type="button"
                    onClick={() => invoiceUploadInputRef.current?.click?.()}
                  >
                    Browse
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedFile(null);
                      setInvoiceList([]);
                      setUserStatus({});
                      setUploadError("");

                      if (invoiceUploadInputRef.current) {
                        invoiceUploadInputRef.current.value = "";
                      }
                    }}
                    className={classes.clearBtn}
                  >
                    Clear
                  </button>
                )}
              </div>

              <p
                className={classes.linkBtn}
                onClick={() => saveAs(InvoiceSampleItemsList, "Invoice Sample.xlsx")}
              >
                Download Sample Excel
              </p>
            </div>

            {selectedFile && (
              <div className={classes.generateAction}>
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={loading}
                  sx={{
                    fontFamily: "gilroyBold",
                    fontSize: "13px",
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 2.5,
                    py: 1,
                  }}
                >
                  {loading ? "Generating..." : "Generate Invoices"}
                </Button>
              </div>
            )}

            {uploadError && <div className={classes.errorBox}>{uploadError}</div>}

            {Object.keys(userStatus).length > 0 && (
              <div className={classes.statusBox}>
                <strong>Status</strong>

                <ul>
                  {Object.entries(userStatus).map(([email, status], i) => (
                    <li key={i}>
                      <span>{email}</span>
                      <b>{status}</b>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {invoiceList.length > 0 && (
              <div className={classes.invoiceTableWrapper}>
                <table className={classes.invoiceTable}>
                  <thead>
                    <tr>
                      <th>Merchant Code / Supplier ID</th>
                      <th>Merchant Name / Supplier Name</th>
                      <th>Email</th>
                      <th>Transaction Amount</th>
                      <th>Invoice</th>
                    </tr>
                  </thead>

                  <tbody>
                    {invoiceList.map((invoice, index) => (
                      <tr key={index}>
                        <td>{invoice?.invoice_id}</td>
                        <td>{invoice.vendor?.company_name}</td>
                        <td>{invoice.vendor?.email}</td>
                        <td>₹{invoice.vendor?.amount}</td>
                        <td>
                          <button
                            onClick={() => downloadPDF(invoice)}
                            className={classes.pdfBtn}
                            title="Download PDF"
                          >
                            📄
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div ref={dashboardExportRef} className={classes.dashboardCanvas}>
          <div className={classes.dashboardCardsSection} data-export-section>
            <CountCards />
          </div>

          <div data-export-section className={classes.dashboardGrid}>
            <div className={classes.dashboardGridItem}>
              <InvoiceChart />
            </div>

            <div className={classes.dashboardGridItem}>
              <VendorChart />
            </div>
          </div>

          <div data-export-section className={classes.dashboardGrid}>
            <div className={classes.dashboardGridItem}>
              <VendorStatusPie />
            </div>

            <div className={classes.dashboardGridItem}>
              <VendorOnboardChart />
            </div>
          </div>

          <div className={classes.dashboardFullWidth} data-export-section>
            <div className={classes.dashboardGridItem}>
              <VendorInvoiceDashboard />
            </div>
          </div>

          <div data-export-section className={classes.dashboardTableRow}>
            <div className={classes.dashboardGridItem}>
              <VendorInvoiceTable />
            </div>
          </div>

          <div data-export-section className={classes.dashboardTableRow}>
            <div className={classes.dashboardGridItem}>
              <InvoiceStatusTable />
            </div>
          </div>

          <div data-export-section className={classes.dashboardTableRow}>
            <div className={classes.dashboardGridItem}>
              <PendingESignatureTable />
            </div>
          </div>

          <div data-export-section className={classes.dashboardTableRow}>
            <div className={classes.dashboardGridItem}>
              <MerchantInvoiceDelay />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceAutomation;